import {useRef,useState,useCallback,useEffect} from 'react'
import {useDispatch,useSelector} from 'react-redux'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Tooltip from 'react-bootstrap/Tooltip'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import {useRecorder} from './hooks'
import Webcam from "react-webcam"
import useS3DirectUpload from '../../hooks/useS3DirectUpload'
import {updateExerciseSet,addExerciseResultVideo} from '../../state/events'
import {setData} from './slice'
import useCurrentUser from '../../hooks/useCurrentUser'
import { useDebounceCallback } from '@react-hook/debounce'

const SetOptions = ({sets,activeSet})=>{
	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const dispatch = useDispatch()
	const handleClick = (setIdx)=>{
		dispatch(setData({setIdx}))
	}
	return (
		<div>
			Set #:
			<ButtonGroup className="ml-2">
				{sets.map((set,i)=>{
					const videoExists = Boolean(set.result_video_url)
					return (
						<OverlayTrigger overlay={
							<Tooltip>
								{
									isClient ?
									//client only copy
									'Edit & record exercise'
									:
									//trainer only copy
									(
										videoExists ?
										`Assess client recording for set ${i+1} & provide feedback` :
										`No recording for set ${i+1}`
									)
								}
							</Tooltip>
						}>
							<Button 
								variant={activeSet===i?'primary':'outline-primary'}
								style={{
									cursor:videoExists ? 'pointer' : 'default'
								}}
								//disable button if no video exists
								onClick={()=>{
									//if no recording & not a client do nothing
									if(!videoExists&&!isClient) return
									handleClick(i)}
								}
								key={set.id}
								size="sm" 
							>
								{i+1}
							</Button>
						</OverlayTrigger>
					)
				}
			)}
			</ButtonGroup>
		</div>
	)
}

/**
 * Component for existing 
 */
const ExistingRecording = ({url,handleDelete,handleReplace,exercise,workout,set})=>{
	const dispatch = useDispatch()
	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const [resultNotes,setResultNotes] = useState('')
	useEffect(()=>{
		if(!set) return
		setResultNotes(set.result_video_notes)
	},[set,exercise])
	const handleNotesChange = useCallback(result_video_notes => {
		const update = {result_video_notes}
		dispatch(updateExerciseSet({
			exercise_id:exercise.exercise_id,
			workout_id:workout.workout_id,
			type:exercise.type,
			update,
			id:set.id,
		}))
	},[set])

	const debouncedHandleNotesChange = useDebounceCallback(handleNotesChange,300)
	return (
		<div>
			<strong className="d-block text-center">
				Current Recording
			</strong>
			<video className="d-block w-100 rounded" controls src={url}/>
			{
				isClient &&
				<div className="text-right">
					<ButtonGroup className="mt-1" size="sm">
						<Button 
							onClick={handleReplace}
							variant="outline-dark">
							<i className="fas fa-exchange-alt mr-1"/>
							Replace
						</Button>
						<Button 
							onClick={handleDelete}
							variant="outline-danger">
							<i className="fa fa-trash mr-1"/>
							Delete
						</Button>
					</ButtonGroup>
				</div>
			}
			<hr className="my-2"/>
			{
				isClient?
				<div className="w-100 mt-1">
					{
						set?.result_video_notes? 
						set?.result_video_notes:
						'no feedback yet'
					}
				</div> 
				:
				<Form.Control 
					onChange={ e => {
						setResultNotes(e.target.value)
						debouncedHandleNotesChange(e.target.value)
					}}
					placeholder="Notes & comments" 
					value={resultNotes}
					className="mt-2"
					as="textarea" 
				/>
			}
		</div>
	)
}

const RecordingPreview = ({file,handleDiscard,handleSave,status})=>{
	return (
		<div>
			<strong>Preview</strong>
			<video 
				src={URL.createObjectURL(file)}
				className="d-block w-100 rounded" 
				controls 
			/>
			<ButtonGroup size="sm">
				<Button 
					disabled={status==='pending'}
					onClick={handleDiscard}
					variant="outline-danger">
					Discard
				</Button>
				<Button 
					disabled={status==='pending'}
					onClick={handleSave}
					variant="outline-success">
					Save {status==='pending'&&
						<Spinner animation="border" size="sm"/>
					}
				</Button>
			</ButtonGroup>
		</div>
	)
}

const ExerciseRecorder = ({handleStopRecording,handleCancel})=>{
	const webcamRef = useRef(null)
	const mediaRecorderRef = useRef(null)
	const [capturing, setCapturing] = useState(false)
	const [recordedChunks, setRecordedChunks] = useState([])

	const handleStartCaptureClick = useCallback(() => {
		setRecordedChunks([])
		setCapturing(true);
		mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream);
		mediaRecorderRef.current.addEventListener(
			"dataavailable",
			handleDataAvailable
		);
		//pull data ever 500ms
		mediaRecorderRef.current.start(500);
	}, [webcamRef, setCapturing, mediaRecorderRef])

	const handleDataAvailable = useCallback(
		({ data }) => {
			if (data.size > 0) {
				setRecordedChunks((prev) => prev.concat(data));
			}
		},
		[setRecordedChunks]
	)
	const handleStopCaptureClick = useCallback(async () => {
		setCapturing(false)
		mediaRecorderRef.current.stop()
		if(recordedChunks.length){ 
			const mimeType = recordedChunks[0].type.split(';')[0]
			const mediaType = mimeType.split('/')[0]
			const extension = mimeType.split('/')[1]

			const blob = await new Blob(recordedChunks, { type : mimeType })
			
			setRecordedChunks([])
			if(blob){
				handleStopRecording(blob)
			}
		}
	}, [mediaRecorderRef, webcamRef, setCapturing,recordedChunks])

	const handleCancelLocal = useCallback(async () => {
		handleCancel()
		setRecordedChunks([])
	}, [setRecordedChunks])

	return (
		<div className="d-flex flex-column w-100 my-1 align-items-center">
			<strong>Webcam</strong>
			<Webcam className="w-100 rounded" ref={webcamRef}/>
			<ButtonGroup size="sm">
				<Button 
					onClick={handleCancelLocal}
					variant="outline-dark">
					Cancel
				</Button>
				{
					capturing ?
					<Button 
						onClick={()=>{
							handleStopCaptureClick()
						}}
						variant="outline-danger">
						<i className="fas fa-stop-circle mr-1"/>
						End Recording
					</Button> :
					<Button 
						onClick={handleStartCaptureClick}
						variant="outline-danger">
						<i className="fa fa-circle mr-1"/>
						Start Recording
					</Button>
				}
			</ButtonGroup>
		</div>
	)
}

const UploadOptions = ({handleChooseWebcam,handleFileSelected,handleCancel,showCancel})=>{
	return (
		<div className="text-center mt-2">
			<div className="mb-2">
				<strong>Upload Options</strong>
			</div>
			<Button 
				onClick={handleChooseWebcam}
				variant="outline-primary" 
				block
			>
				Record from webcam
			</Button>
			<div>-</div>
			<Form.File 
				onChange={e=>{ handleFileSelected(e.target.files[0]) }}
				label="Upload from files" 
				custom
			/>

			{showCancel&&
				<>
					<div>-</div>
					<Button variant="outline-danger" onClick={handleCancel} block>
						Back to recording
					</Button>
				</>
			}
		</div>
	)
}

/**
	Logic
	- if upload exists then view & give option to replace or delete 
	- if replacing or no recording exists then present select from files or record from web cam
	- if select from files then open browser
	- if recording then open webcam view with recording options
	- if recording stopped or file selected then display preview with preview options: discard/save
 */
const ExerciseResultVideo = ({})=>{
	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const dispatch = useDispatch()
	const {show,workout,exercise,set,toggleShow,setIdx} = useRecorder()
	const handleClose = ()=>{ dispatch(toggleShow()) }
	//modes: existing | webcam | uploadOptions | preview
	const [mode,setMode] = useState('uploadOptions')
	const [status,setStaus] = useState('idle')
	const [previewData,setPreviewData] = useState(null)

	useEffect(()=>{
		if(!set) return
		setMode(
			Boolean(set.result_video_url) ? 
			'existing' :
			'uploadOptions'
		)
	},[set,exercise,setIdx])

	const handleSave = useCallback(async () => {
		setStaus('pending')
		const mimeType = previewData.type.split(';')[0]
		const mediaType = mimeType.split('/')[0]
		const extension = mimeType.split('/')[1]

		//preview data is not always a file somtimes it is a blob
		//so for these we need to convert to a file before sending
		//to server
		const result_video = (
			previewData instanceof Blob ?
			await new File([previewData],`video.${extension}`) :
			previewData
		)
		dispatch(addExerciseResultVideo({
			exercise_id:exercise.exercise_id,
			workout_id:workout.workout_id,
			type:exercise.type,
			result_video,
			id:set.id,
		})).then(()=>{
			setStaus('idle')
			setPreviewData(null)
		})
	}, [previewData])

	const handleNotesChange = useCallback(result_video_notes => {
		const update = {result_video_notes}
		dispatch(updateExerciseSet({
			exercise_id:exercise.exercise_id,
			workout_id:workout.workout_id,
			type:exercise.type,
			update,
			id:set.id,
		}))
	},[set])

	const debouncedHandleNotesChange = useDebounceCallback(handleNotesChange,300)

	const handleDelete = useCallback(async () => {
		if(!window.confirm('Are you sure you want to delete this exercise recording?')) return
		const update = {result_video_url:''}
		dispatch(updateExerciseSet({
			exercise_id:exercise.exercise_id,
			workout_id:workout.workout_id,
			type:exercise.type,
			update,
			id:set.id,
		}))
	})

	return (
		<Modal onHide={handleClose} show={show} centered>
			<Modal.Header closeButton>
				{workout?.note} - {exercise?.exercise_name}
			</Modal.Header>
			<Modal.Body>
				<SetOptions sets={exercise?.set_data||[]} activeSet={setIdx}/>
				{mode==='uploadOptions'&&
					<UploadOptions
						handleChooseWebcam={()=>{setMode('webcam')}}
						handleFileSelected={file=>{
							setPreviewData(file)
							setMode('preview')
						}}
						handleCancel={()=>{setMode('existing')}}
						showCancel={Boolean(set?.result_video_url)}
					/>
				}
				{mode==='webcam'&&
					<ExerciseRecorder
						//this handler expects ExerciseRecorder to pass
						//in a file generated from the webcam
						handleStopRecording={(file)=>{
							setPreviewData(file)
							setMode('preview')
						}}
						handleCancel={()=>{
							setMode('uploadOptions')
						}}
					/>
				}
				{mode==='existing'&&
					<ExistingRecording 
						handleReplace={()=>{setMode('uploadOptions')}}
						handleDelete={handleDelete}
						url={set?.result_video_url}
						exercise={exercise}
						workout={workout}
						set={set}
					/>
				}
				{mode==='preview'&&
					<RecordingPreview 
						handleDiscard={()=>{
							setMode('uploadOptions')
							setPreviewData(null)
						}}
						handleSave={handleSave}
						file={previewData}
						status={status}
					/>
				}
			</Modal.Body>
			
			<Modal.Footer>
				<small className="d-block text-center w-100 text-danger mt-1">
					<strong className="mr-1">Note:</strong>
					Recordings are deleted 7 days after being viewed by trainers
				</small>
			</Modal.Footer>
		</Modal>
	)
}

export default ExerciseResultVideo